import { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { LayoutGrid, Brush, Image as ImageIcon, ShoppingBag, Settings as Cog, ShieldCheck, LogOut, ExternalLink, Menu, X, MailWarning } from "lucide-react";
import { Logo } from "../../components/Logo";
import { useAuth } from "../../context/AuthContext";
import { api, asset } from "../../lib/api";
import { toast } from "sonner";

const NAV = [
  { to: "/dashboard", end: true, icon: LayoutGrid, label: "overview", id: "overview" },
  { to: "/dashboard/customize", icon: Brush, label: "customize", id: "customize" },
  { to: "/dashboard/images", icon: ImageIcon, label: "image host", id: "images" },
  { to: "/dashboard/store", icon: ShoppingBag, label: "store", id: "store" },
  { to: "/dashboard/settings", icon: Cog, label: "settings", id: "settings" },
];

const Side = ({ user, logout, close }) => (
  <aside className="flex h-full w-64 flex-col border-r border-[#1a1a22] bg-[#09090b] p-5">
    <div className="flex items-center justify-between"><Logo /><button className="md:hidden" onClick={close}><X className="h-5 w-5 text-zinc-400" /></button></div>
    <nav className="mt-10 flex flex-1 flex-col gap-1">
      {[...NAV, ...(user.role === "admin" ? [{ to: "/dashboard/admin", icon: ShieldCheck, label: "admin", id: "admin" }] : [])].map((n) => (
        <NavLink key={n.id} to={n.to} end={n.end} onClick={close} data-testid={`sidebar-${n.id}-link`}
          className={({ isActive }) => `group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors ${isActive ? "bg-[#ff3b91]/10 text-white shadow-[inset_2px_0_0_#ff3b91]" : "text-zinc-400 hover:bg-white/[0.03] hover:text-white"}`}>
          <n.icon className="h-4 w-4" />{n.label}
        </NavLink>
      ))}
    </nav>
    <a href={`/${user.username}`} target="_blank" rel="noreferrer" data-testid="sidebar-view-profile-link" className="btn-ghost mb-3 w-full text-xs"><ExternalLink className="h-3.5 w-3.5" /> view my page</a>
    <div className="flex items-center gap-3 rounded-xl border border-[#1a1a22] p-3">
      {user.avatar_url ? <img src={asset(user.avatar_url)} alt="" className="h-9 w-9 rounded-full object-cover" /> : <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#ff3b91]/20 font-display font-bold text-[#ff3b91]">{user.username[0].toUpperCase()}</div>}
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-white" data-testid="sidebar-username">@{user.username}</div>
        <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">{user.is_premium ? <span className="text-[#ff3b91]">premium</span> : "free"}</div>
      </div>
      <button onClick={logout} data-testid="sidebar-logout-button" className="text-zinc-500 transition-colors hover:text-[#ff3b91]"><LogOut className="h-4 w-4" /></button>
    </div>
  </aside>
);

const VerifyBanner = () => {
  const [sent, setSent] = useState(false);
  const resend = async () => {
    try { await api.post("/auth/resend-verification"); setSent(true); toast.success("Verification email sent"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send"); }
  };
  return (
    <div data-testid="verify-email-banner" className="mb-8 flex flex-wrap items-center gap-3 rounded-2xl border border-[#ff3b91]/25 bg-[#ff3b91]/[0.06] px-5 py-3 text-sm text-pink-100">
      <MailWarning className="h-4 w-4 text-[#ff3b91]" />
      <span className="flex-1">Verify your email to unlock purchases. Check your inbox.</span>
      <button disabled={sent} onClick={resend} className="font-semibold text-[#ff3b91] hover:underline disabled:opacity-50" data-testid="resend-verification-button">{sent ? "sent" : "resend link"}</button>
    </div>
  );
};

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  return (
    <div className="flex min-h-screen bg-[#080808] text-white">
      <div className="sticky top-0 hidden h-screen md:block"><Side user={user} logout={logout} close={() => {}} /></div>
      {open && <div className="fixed inset-0 z-50 flex md:hidden"><Side user={user} logout={logout} close={() => setOpen(false)} /><div className="flex-1 bg-black/60" onClick={() => setOpen(false)} /></div>}
      <main className="min-w-0 flex-1">
        <div className="flex h-14 items-center justify-between border-b border-[#1a1a22] px-5 md:hidden">
          <Logo /><button onClick={() => setOpen(true)} data-testid="mobile-menu-button"><Menu className="h-5 w-5" /></button>
        </div>
        <div className="mx-auto max-w-6xl px-5 py-8 md:px-10 md:py-12">
          {!user.email_verified && <VerifyBanner />}
          <Outlet />
        </div>
      </main>
    </div>
  );
}
